import { all, select, takeEvery, fork, call, put, delay } from 'redux-saga/effects'

// action
import { configActions, customerActions, routeActions } from '@redux/store/index'

// api
import { mainCustomerApi, customerRoleApi, ResType } from '@api'

// lib
import { Logger } from '@utils'

// type
import {
	Customer,
	CustomerCreationType,
	CustomerPasswordUpdatePathType,
	CustomerPasswordUpdateType,
	CustomerRoleSetPathType,
	CustomerRoleSetType,
	CustomerType,
} from '@schemas'
import { CustomerStateType, CustomerCombinedUpdateType } from '@redux/types'

const apiGetCustomers = async (): Promise<ResType | undefined> => {
	const res: ResType | undefined = await mainCustomerApi.getCustomer()
	return res
}

const apiCreateCustomer = async (
	body: CustomerCreationType,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await mainCustomerApi.createCustomer({ body })
	return res
}

const apiUpdateCustomer = async (
	body: CustomerCombinedUpdateType,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await mainCustomerApi.updateCustomer({
		params: { customerId: body.customerId },
		body: {
			name: body.name,
			email: body.email,
			confirmEmailNewsYn: body.confirmEmailNewsYn,
		},
	})
	return res
}

const apiUpdateCustomerPassword = async (
	body: CustomerPasswordUpdatePathType & CustomerPasswordUpdateType,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await mainCustomerApi.updateCustomerPassword({
		params: { customerId: body.customerId },
		body: {
			password: body.password,
		},
	})
	return res
}

const apiSetCustomerRoles = async (
	body: CustomerRoleSetPathType & CustomerRoleSetType,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await customerRoleApi.setCustomerRoles({
		params: { customerId: body.customerId },
		body: { roleCodes: body.roleCodes },
	})
	return res
}

const apiDeleteCustomer = async (
	body: CustomerCombinedUpdateType,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await mainCustomerApi.deleteCustomer({
		params: { customerId: body.customerId },
	})
	return res
}

function* triggerInitCustomer() {
	try {
		yield put(customerActions.CUSTOMER_LIST_STATUS('loading'))
		const apiRes: ResType = yield call(apiGetCustomers)

		if (apiRes.status === 200) {
			const tempCustomerList = apiRes.data[0] !== undefined ? (apiRes.data as any[]) : []
			const newCustomerList = tempCustomerList.map(
				aCustomer => new Customer(aCustomer as CustomerType),
			)
			yield put(customerActions.CUSTOMER_LIST(newCustomerList))

			if (apiRes.data.length > 0) {
				yield put(customerActions.CUSTOMER_LIST_STATUS('ok'))
			} else {
				yield put(customerActions.CUSTOMER_LIST_STATUS('empty'))
			}
		} else {
			yield put(customerActions.CUSTOMER_LIST_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateResetPassword() {
	try {
		const updateInfo: CustomerStateType['updateResetPassword'] = yield select(
			state => state.customer.updateResetPassword,
		)

		const apiRes: ResType = yield call(apiUpdateCustomerPassword, updateInfo)

		if (apiRes !== undefined && apiRes.status === 201) {
			yield put(
				configActions.TOAST({ isShow: true, message: '패스워드가 업데이트 되었습니다.' }),
			)
			delay(2000)
			yield put(routeActions.NAVIGATE_PATH('/main/customer'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateCustomer() {
	try {
		const body: CustomerStateType['updateCustomer'] = yield select(
			state => state.customer.updateCustomer,
		)

		switch (body.iudFlag) {
			case 'I':
				yield call(apiCreateCustomer, {
					name: body.name,
					email: body.email,
					confirmEmailNewsYn: body.confirmEmailNewsYn ? 1 : 0,
					// note: 임시 패스워드 -- 어떻게 제대로 추가하지?
					password: '0000',
				} as CustomerCreationType)
				break
			case 'U':
				yield call(apiUpdateCustomer, {
					customerId: body.customerId,
					name: body.name,
					email: body.email,
					// note: confirmEmailNewsYn은 number 타입으로 정의했지만 Grid 컴포넌트에서 boolean 타입으로 사용하고 있음
					confirmEmailNewsYn: body.confirmEmailNewsYn ? 1 : 0,
				} as CustomerCombinedUpdateType)
				break
			case 'D':
				yield call(apiDeleteCustomer, {
					customerId: body.customerId,
				} as CustomerCombinedUpdateType)
				break
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/**
 * 고객 유형(역할) 집합 저장. 결과가 어떻든 목록을 다시 읽는다 —
 * 그리드는 이미 새 값을 그려 놓았으므로 서버가 거절(409 = Dealer+Distributor)했으면 되돌려야 한다.
 */
function* updateCustomerRoles() {
	try {
		const body: CustomerStateType['updateCustomerRoles'] = yield select(
			state => state.customer.updateCustomerRoles,
		)

		const apiRes: ResType | undefined = yield call(apiSetCustomerRoles, body)

		if (apiRes !== undefined && [200, 201].includes(apiRes.status)) {
			yield put(
				configActions.TOAST({ isShow: true, message: '고객 유형이 저장되었습니다.' }),
			)
		} else if (apiRes !== undefined && apiRes.status === 409) {
			yield put(
				configActions.TOAST({
					isShow: true,
					message: 'Dealer 와 Distributor 는 함께 지정할 수 없습니다.',
				}),
			)
		} else {
			yield put(
				configActions.TOAST({ isShow: true, message: '고객 유형 저장에 실패했습니다.' }),
			)
		}

		yield call(triggerInitCustomer)
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(customerActions.TRIGGER_INIT_CUSTOMER_PAGE, triggerInitCustomer)
	yield takeEvery(customerActions.UPDATE_RESET_PASSWORD, updateResetPassword)
	yield takeEvery(customerActions.UPDATE_CUSTOMER, updateCustomer)
	yield takeEvery(customerActions.UPDATE_CUSTOMER_ROLES, updateCustomerRoles)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
